import { GetStaticProps } from "next";
import { useTranslation } from "next-i18next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ScannerSection from "@/sections/build/scanner-section";
import Layout from "../components/Layouts/Layout";
import Meta from "../components/Layouts/Meta";
import Section2 from "../sections/build/section-2";
import { Badge } from "@/components/ui/badge";
import { CURRENT_BROKER_NAME } from "@/config/logos";
import { capitalizeFirstLetter } from "@/helpers/helperFunctions";

const TEST_TABS = {
  STRATEGIES: "strategies",
  SCANNERS: "scanners",
};

const DEFAULT_TAB = TEST_TABS.STRATEGIES;

const TestPage = () => {
  const { t } = useTranslation("test");
  const router = useRouter();
  const [selectedTab, setSelectedTab] = useState<string>(DEFAULT_TAB);

  // Set initial tab based on query param
  useEffect(() => {
    if (router.isReady) {
      const tabParam = router.query.tab as string;
      if (Object.values(TEST_TABS).includes(tabParam)) {
        setSelectedTab(tabParam);
      } else {
        // Default to STRATEGIES if param is invalid
        setSelectedTab(DEFAULT_TAB);
      }
    }
  }, [router.isReady, router.query.tab]);

  // Update the URL when tab changes
  const handleTabChange = (tab: string) => {
    setSelectedTab(tab);
    router.replace(
      {
        pathname: router.pathname,
        query: { ...router.query, tab },
      },
      undefined,
      { shallow: true }
    );
  };

  return (
    <Layout
      meta={
        <Meta
          title={capitalizeFirstLetter(CURRENT_BROKER_NAME)}
          description={capitalizeFirstLetter(CURRENT_BROKER_NAME)}
        />
      }
    >
      <div className="mx-auto">
        <Tabs value={selectedTab} onValueChange={handleTabChange}>
          <TabsList className="w-fit md:w-1/3 my-2 mx-2 md:mx-0">
            <TabsTrigger value={TEST_TABS.STRATEGIES} className="!grow w-auto">
              {t("Strategies")}
            </TabsTrigger>
            <TabsTrigger value={TEST_TABS.SCANNERS} className="!grow w-auto">
              Scanners
            </TabsTrigger>
          </TabsList>
          <TabsContent value={TEST_TABS.SCANNERS}>
            <ScannerSection />
          </TabsContent>
          <TabsContent value={TEST_TABS.STRATEGIES}>
            <Section2 />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  return {
    props: {
      ...(await serverSideTranslations(locale || "en", [
        "build",
        "market-screener",
        "layout",
        "common",
        "test",
        "trade",
        "walk-through",
        "alert-strategy",
        "add-ons-form",
      ])),
    },
    revalidate: 60,
  };
};

export default TestPage;
